/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Drawer as MuiDrawer, styled, Theme, CSSObject } from '@mui/material'
import { DrawerToggleButton } from '@components/systems/drawer/DrawerToggleButton'
import { DrawerHeader } from '@components/systems/drawer/DrawerHeader'
import { DrawerMenuList } from '@components/systems/drawerMenu/DrawerMenuList'
import { DrawerBottomView } from '@components/systems/drawer/DrawerBottomView'
import { mono, boMobile, boZ } from '@components/systems/backoffice/backoffice.tokens'
import { BoShellContext, useBoShell } from '@components/systems/backoffice/BoShellContext'

import { useAppDispatch, useAppSelector, configActions } from '@redux/index'

interface PropsType {
	/**
	 * desktop = permanent 240/64(지금 그대로) · rail = permanent 64 고정(태블릿) ·
	 * temporary = 화면을 덮는 임시 드로어(폰 · 태블릿 펼침) (RESPONSIVE_PLAN §4)
	 */
	mode?: 'desktop' | 'rail' | 'temporary'
}

/**
 * 사이드바. surface 위에 우측 헤어라인 하나 — 그림자·색 띠 없음.
 * 관리자 모드 표시는 상단 바(MainNav)가 맡는다 — 사이드바는 두 모드가 같다.
 * 열림/닫힘 폭·스크롤 위치 복원은 그대로다.
 */
export const Drawer = (props: PropsType) => {
	const mode = props.mode ?? 'desktop'

	// redux dispatch
	const reduxDispatch = useAppDispatch()
	const shell = useBoShell()

	// redux state
	const drawerWidth: number = useAppSelector(state => state.config.drawerWidth)
	const closedDrawerWidth: number = useAppSelector(
		state => state.config.closedDrawerWidth,
	)
	const isOpenDrawerRedux: boolean = useAppSelector(state => state.config.isOpenDrawer)
	const drawerScrollTop: number = useAppSelector(state => state.config.drawerScrollTop)

	const isOpenDrawer = mode === 'desktop' ? isOpenDrawerRedux : mode === 'temporary'

	const drawerScrollRef = React.useRef<any | null>(null)
	const refDrawerScrollTop: any = React.useMemo(() => {
		return drawerScrollRef.current !== null && drawerScrollRef.current.scrollTop
			? drawerScrollRef.current.scrollTop
			: null
	}, [drawerScrollRef.current])

	React.useEffect(() => {
		if (drawerScrollRef.current && drawerScrollRef.current.scrollTop !== undefined) {
			drawerScrollRef.current.scrollTop = drawerScrollTop
		}
	}, [drawerScrollTop])

	React.useEffect(() => {
		if (drawerScrollRef.current !== null) {
			const div: any = drawerScrollRef.current

			if (refDrawerScrollTop) {
				div.scrollTop = refDrawerScrollTop
				let scrollTimer: ReturnType<typeof setTimeout>
				div.addEventListener('scroll', () => {
					if (scrollTimer !== undefined) {
						clearTimeout(scrollTimer)
					}

					scrollTimer = setTimeout(function () {
						reduxDispatch(configActions.DRAWER_SCROLL_TOP(refDrawerScrollTop))
					}, 150)
				})
			}
		}
	}, [refDrawerScrollTop, drawerScrollRef.current, drawerScrollTop])

	const openedMixin = React.useCallback(
		(theme: Theme): CSSObject => ({
			width: drawerWidth,
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: 200,
			}),
			overflowX: 'inherit',
			overflow: 'inherit',
		}),
		[drawerWidth],
	)

	const closedMixin = React.useCallback(
		(theme: Theme): CSSObject => ({
			transition: theme.transitions.create('width', {
				easing: theme.transitions.easing.sharp,
				duration: 200,
			}),
			overflowX: 'inherit',
			overflow: 'inherit',
			width: closedDrawerWidth,
		}),
		[closedDrawerWidth],
	)

	const DrawerComponent = styled(MuiDrawer, {
		shouldForwardProp: prop => prop !== 'open',
	})(({ theme, open }) => ({
		width: drawerWidth,
		flexShrink: 0,
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		'& .MuiDrawer-paper': {
			background: mono.surface,
			borderRight: `1px solid ${mono.line}`,
			boxShadow: 'none',
		},
		...(open && {
			...openedMixin(theme),
			'& .MuiDrawer-paper': {
				...openedMixin(theme),
				background: mono.surface,
				borderRight: `1px solid ${mono.line}`,
			},
		}),
		...(!open && {
			...closedMixin(theme),
			'& .MuiDrawer-paper': {
				...closedMixin(theme),
				background: mono.surface,
				borderRight: `1px solid ${mono.line}`,
			},
		}),
	}))

	const content = (
		<>
			{mode === 'desktop' ? <DrawerToggleButton /> : null}
			<DrawerHeader />

			<View className="menuListView" ref={drawerScrollRef}>
				<DrawerMenuList />
			</View>

			<DrawerBottomView />
		</>
	)

	if (mode === 'temporary') {
		// 사이드바 안 컴포넌트가 "펼침" 으로 그리도록 컨텍스트만 덮는다
		return (
			<BoShellContext.Provider value={{ ...shell, drawerOpen: true }}>
				<MuiDrawer
					variant="temporary"
					anchor="left"
					open={shell.mobileDrawerOpen}
					onClose={() => shell.setMobileDrawerOpen(false)}
					ModalProps={{ keepMounted: false }}
					css={style}
					sx={{ zIndex: boZ.drawer }}
					PaperProps={{
						className: 'boTempDrawerPaper',
						sx: {
							width: boMobile.drawerW,
							background: mono.surface,
							borderRight: `1px solid ${mono.line}`,
							boxShadow: 'none',
							display: 'flex',
							flexDirection: 'column',
						},
					}}
				>
					{content}
				</MuiDrawer>
			</BoShellContext.Provider>
		)
	}

	return (
		<BoShellContext.Provider value={{ ...shell, drawerOpen: isOpenDrawer }}>
			<DrawerComponent
				variant="permanent"
				anchor="left"
				open={isOpenDrawer}
				css={style}
				className={mode === 'rail' ? 'boRail' : ''}
			>
				{content}
			</DrawerComponent>
		</BoShellContext.Provider>
	)
}

const style = css({
	zIndex: 7,
	overflow: 'inherit',

	'.MuiDrawer-paper': {
		display: 'flex',
		flexDirection: 'column',
	},

	'.menuListView': {
		flex: '1 1 auto',
		minHeight: 0,
		overflowY: 'auto',
		overflowX: 'hidden',
		// 로고 행(헤어라인)과 첫 메뉴 사이 숨 — 두 모드 공통
		paddingTop: 20,
		paddingBottom: 8,
	},

	// 임시 드로어 — 메뉴 항목 터치 목표 44
	'.boTempDrawerPaper .boMenuItem': {
		minHeight: 44,
		height: 44,
		fontSize: 15,
	},
	'.boTempDrawerPaper .boMenuItem.depth2, .boTempDrawerPaper .boMenuItem.depth3': {
		minHeight: 40,
		height: 40,
		fontSize: 14,
	},
})
